import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import CustomerInfo from '../components/customers/CustomerInfo';
import CustomerAppointments from '../components/customers/CustomerAppointments';
import CustomerTreatmentRecords from '../components/customers/CustomerTreatmentRecords';
import customerService from '../services/CustomerService';
import appointmentService from '../services/AppointmentService';
import treatmentRecordService from '../services/TreatmentRecordService';
import { useAuth } from '../contexts/AuthContext';
import {
  Card,
  Button,
  Modal,
  Input,
  Select,
  Alert
} from '../components/ui';
import treatmentService from '../services/TreatmentService';
import practitionerService from '../services/PractitionerService';
import venueService from '../services/VenueService';

/**
 * CustomerDetailPage displays detailed information about a specific customer,
 * including their basic info, appointment history, and treatment records
 * @returns {React.ReactElement} CustomerDetailPage component
 */
const CustomerDetailPage = () => {
  const { id: customerId } = useParams();
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();

  // State for customer data
  const [customer, setCustomer] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [treatmentRecords, setTreatmentRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phoneNumber: '',
    gender: '女',
    age: '',
    address: '',
    notes: '',
    customerType: 'new',
    height: '',
    weight: '',
    lifestyle: { diet: '', sleep: '', exercise: '' },
    familyHistory: '',
    currentSymptoms: '',
    intendedProject: '',
    intendedPractitioner: '',
    specialNeeds: ''
  });
  const [formErrors, setFormErrors] = useState({});

  // Check permissions
  const canViewCustomer = hasPermission('customers', 'read');
  const canEditCustomer = hasPermission('customers', 'write');

  // Fetch customer data
  useEffect(() => {
    if (!canViewCustomer) {
      setError('您没有查看客户详情的权限');
      setLoading(false);
      return;
    }

    async function fetchCustomerData() {
      setLoading(true);
      try {
        // Fetch customer details
        const customerData = customerService.getCustomerById(customerId);
        if (!customerData) {
          setError('找不到此客户');
          return;
        }
        setCustomer(customerData);

        // Initialize form data
        setFormData({
          name: customerData.name || '',
          phoneNumber: customerData.phoneNumber || '',
          gender: customerData.gender || '女',
          age: customerData.age || '',
          address: customerData.address || '',
          notes: customerData.notes || '',
          customerType: customerData.customerType || 'new',
          height: customerData.height || '',
          weight: customerData.weight || '',
          lifestyle: customerData.lifestyle || { diet: '', sleep: '', exercise: '' },
          familyHistory: (customerData.familyHistory && customerData.familyHistory.join) ? customerData.familyHistory.join(', ') : '',
          currentSymptoms: (customerData.currentSymptoms && customerData.currentSymptoms.join) ? customerData.currentSymptoms.join(', ') : '',
          intendedProject: customerData.intendedProject || '',
          intendedPractitioner: customerData.intendedPractitioner || '',
          specialNeeds: customerData.specialNeeds || ''
        });

        // Fetch customer appointments
        const appointmentsData = appointmentService.getAppointmentsByCustomer(customerId);
        // Add run-time compatibility for appointmentTime and display fields
        const processedAppointments = appointmentsData.map(app => {
          const treatment = treatmentService.getTreatmentById(app.treatmentId);
          const practitioner = practitionerService.getPractitionerById(app.practitionerId);
          const venue = venueService.getVenueById(app.venueId);

          // Calculate duration if not present
          let duration = app.duration;
          if (!duration && app.startTime && app.endTime) {
            const start = new Date(app.startTime);
            const end = new Date(app.endTime);
            duration = Math.round((end - start) / 60000);
          }

          return {
            ...app,
            appointmentTime: app.startTime || app.appointmentTime,
            treatmentName: treatment ? treatment.name : undefined,
            practitionerName: practitioner ? practitioner.name : undefined,
            venueName: venue ? venue.name : undefined,
            duration
          };
        });
        setAppointments(processedAppointments);

        // Fetch customer treatment records
        const recordsData = treatmentRecordService.getTreatmentRecordsByCustomer(customerId);
        setTreatmentRecords(recordsData);

        setError(null);
      } catch (err) {
        console.error('Error fetching customer data:', err);
        setError('获取客户数据失败');
      } finally {
        setLoading(false);
      }
    }

    fetchCustomerData();
  }, [customerId, canViewCustomer]);

  // Handle input change for form
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Handle nested lifestyle fields
    if (name.startsWith('lifestyle.')) {
      const key = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        lifestyle: {
          ...prev.lifestyle,
          [key]: value
        }
      }));
      if (formErrors[name]) {
        setFormErrors(prev => ({ ...prev, [name]: '' }));
      }
      return;
    }
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = '请输入客户姓名';
    }

    if (!formData.phoneNumber.trim()) {
      errors.phoneNumber = '请输入联系电话';
    } else if (!/^1[3-9]\d{9}$/.test(formData.phoneNumber.trim())) {
      errors.phoneNumber = '请输入正确的手机号码';
    }

    if (formData.age && (formData.age < 0 || formData.age > 120)) {
      errors.age = '请输入有效的年龄';
    }

    return errors;
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!canEditCustomer) {
      setError('您没有编辑客户的权限');
      return;
    }

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      customerService.updateCustomer(customerId, {
        ...formData,
        familyHistory: formData.familyHistory ? formData.familyHistory.split(',').map(s => s.trim()).filter(Boolean) : [],
        currentSymptoms: formData.currentSymptoms ? formData.currentSymptoms.split(',').map(s => s.trim()).filter(Boolean) : [],
      });

      // Update local state
      setCustomer(prev => ({
        ...prev,
        ...formData
      }));

      setShowEditModal(false);
      setFormErrors({});
    } catch (err) {
      console.error('Error updating customer:', err);
      setError('保存客户信息失败');
    }
  };

  // Handle create appointment
  const handleCreateAppointment = () => {
    navigate(`/appointments/new?customerId=${customerId}`);
  };

  // Handle create treatment record
  const handleCreateTreatmentRecord = () => {
    navigate(`/treatment-records/new?customerId=${customerId}`);
  };

  // Gender options
  const genderOptions = [
    { value: '女', label: '女' },
    { value: '男', label: '男' },
    { value: '其他', label: '其他' }
  ];

  if (error && error === '找不到此客户') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-emerald-50/30 to-blue-50/30 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <Card variant="modern" className="max-w-md w-full text-center">
          <div className="mb-6">
            <svg className="h-16 w-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              客户未找到
            </h2>
            <p className="text-gray-600">
              您尝试访问的客户不存在或已被删除
            </p>
          </div>
          <Button onClick={() => navigate('/customers')}>
            返回客户列表
          </Button>
        </Card>
      </div>
    );
  }

  if (!canViewCustomer) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-emerald-50/30 to-blue-50/30 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <Card variant="modern" className="max-w-md w-full text-center">
          <div className="mb-6">
            <svg className="h-16 w-16 text-red-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              权限不足
            </h2>
            <p className="text-gray-600">
              您没有查看客户详情的权限
            </p>
          </div>
          <Button onClick={() => navigate('/')}>
            返回首页
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-emerald-50/30 to-blue-50/30 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <Card variant="glass" className="border-emerald-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/customers')}
                className="shrink-0"
              >
                <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                返回客户列表
              </Button>

              <div className="border-l border-gray-200 pl-4">
                {loading ? (
                  <div className="animate-pulse">
                    <div className="h-8 bg-gray-200 rounded w-48 mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-32"></div>
                  </div>
                ) : customer ? (
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900">
                      {customer.name}
                    </h1>
                    <p className="text-gray-600">
                      客户详情及历史记录
                    </p>
                  </div>
                ) : (
                  <h1 className="text-2xl font-bold text-gray-900">
                    客户详情
                  </h1>
                )}
              </div>
            </div>

            {customer && canEditCustomer && (
              <Button
                onClick={() => setShowEditModal(true)}
                className="shrink-0"
              >
                <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                编辑信息
              </Button>
            )}
          </div>
        </Card>

        {/* Error Display */}
        {error && error !== '找不到此客户' && (
          <Alert variant="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Card variant="modern">
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-2 border-emerald-500 border-t-transparent"></div>
            </div>
          </Card>
        ) : customer ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Customer Info */}
            <div className="lg:col-span-1">
              <CustomerInfo
                customer={customer}
                onEdit={() => setShowEditModal(true)}
                loading={loading}
              />
            </div>

            {/* Appointments and Records */}
            <div className="lg:col-span-2 space-y-6">
              {/* Customer Appointments */}
              <CustomerAppointments
                customerId={customerId}
                appointments={appointments}
                onCreateAppointment={handleCreateAppointment}
                loading={loading}
              />

              {/* Customer Treatment Records */}
              <CustomerTreatmentRecords
                customerId={customerId}
                treatmentRecords={treatmentRecords}
                onCreateRecord={handleCreateTreatmentRecord}
                loading={loading}
              />
            </div>
          </div>
        ) : null}
      </div>

      {/* Edit Customer Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setFormErrors({});
        }}
        title="编辑客户信息"
        size="large"
        footer={
          <div className="flex space-x-3">
            <Button
              variant="secondary"
              onClick={() => {
                setShowEditModal(false);
                setFormErrors({});
              }}
            >
              取消
            </Button>
            <Button
              type="submit"
              form="edit-customer-form"
            >
              保存修改
            </Button>
          </div>
        }
      >
        <form id="edit-customer-form" onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="客户姓名"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              error={formErrors.name}
              required
              placeholder="请输入客户姓名"
            />

            <Input
              label="联系电话"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleInputChange}
              error={formErrors.phoneNumber}
              required
              placeholder="请输入手机号码"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Select
              label="性别"
              options={genderOptions}
              value={formData.gender}
              onChange={handleInputChange}
              name="gender"
            />

            <Input
              label="年龄"
              name="age"
              type="number"
              min="0"
              max="120"
              value={formData.age}
              onChange={handleInputChange}
              error={formErrors.age}
              placeholder="请输入年龄"
            />
          </div>

          {/* New fields group 1: Customer type and age group */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Select
              label="客户类型"
              name="customerType"
              value={formData.customerType}
              onChange={handleInputChange}
              options={[
                { value: 'new', label: '新客户' },
                { value: 'returning', label: '老客户' }
              ]}
            />
            <Input
              label="年龄分组"
              name="ageGroup"
              value={customer?.ageGroup ? (customer.ageGroup === 'youth' ? '青少年' : customer.ageGroup === 'adult' ? '成年人' : '老年人') : ''}
              readOnly
              disabled
              placeholder="自动计算"
            />
          </div>

          {/* New fields group 2: Height, Weight */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="身高 (cm)"
              name="height"
              type="number"
              min="0"
              value={formData.height}
              onChange={handleInputChange}
              placeholder="请输入身高"
            />
            <Input
              label="体重 (kg)"
              name="weight"
              type="number"
              min="0"
              value={formData.weight}
              onChange={handleInputChange}
              placeholder="请输入体重"
            />
          </div>

          {/* New fields group 3: Lifestyle */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Input
              label="饮食习惯"
              name="lifestyle.diet"
              value={formData.lifestyle.diet}
              onChange={handleInputChange}
              placeholder="如：清淡、荤素搭配等"
            />
            <Input
              label="作息习惯"
              name="lifestyle.sleep"
              value={formData.lifestyle.sleep}
              onChange={handleInputChange}
              placeholder="如：早睡早起、熬夜等"
            />
            <Input
              label="运动习惯"
              name="lifestyle.exercise"
              value={formData.lifestyle.exercise}
              onChange={handleInputChange}
              placeholder="如：每周锻炼3次等"
            />
          </div>

          {/* New fields group 4: Family history, symptoms */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">家族病史（逗号分隔）</label>
              <textarea
                name="familyHistory"
                rows={2}
                value={formData.familyHistory}
                onChange={handleInputChange}
                className="w-full px-4 py-3 text-sm border border-gray-300 bg-white text-gray-900 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200"
                placeholder="如：高血压, 糖尿病"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">当前主要健康问题（逗号分隔）</label>
              <textarea
                name="currentSymptoms"
                rows={2}
                value={formData.currentSymptoms}
                onChange={handleInputChange}
                className="w-full px-4 py-3 text-sm border border-gray-300 bg-white text-gray-900 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200"
                placeholder="如：失眠, 乏力, 胃口不好"
              />
            </div>
          </div>

          {/* New fields group 5: Intended project/practitioner, special needs */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Input
              label="意向调理项目"
              name="intendedProject"
              value={formData.intendedProject}
              onChange={handleInputChange}
              placeholder="如：雏英高飞、君子立本等"
            />
            <Input
              label="意向调理师"
              name="intendedPractitioner"
              value={formData.intendedPractitioner}
              onChange={handleInputChange}
              placeholder="如：吴磊、李锡涛等"
            />
            <Input
              label="特殊需求"
              name="specialNeeds"
              value={formData.specialNeeds}
              onChange={handleInputChange}
              placeholder="如：过敏、饮食禁忌等"
            />
          </div>

          <Input
            label="地址"
            name="address"
            value={formData.address}
            onChange={handleInputChange}
            placeholder="请输入客户地址"
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              备注信息
            </label>
            <textarea
              name="notes"
              rows={4}
              value={formData.notes}
              onChange={handleInputChange}
              className="w-full px-4 py-3 text-sm border border-gray-300 bg-white text-gray-900 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200"
              placeholder="添加额外信息，如健康状况、过敏反应、特殊需求等"
            />
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default CustomerDetailPage;